export interface ShareFileOptions {
  url: string;
  /** Pre-fetched file — skips the internal fetch, allowing navigator.share() to be
   *  called without any await before it (preserving the user gesture context). */
  file?: File | null;
}

export const isShareSupported = (url: string): boolean => {
  try {
    return navigator.canShare?.({ url }) ?? false;
  } catch {
    return false;
  }
};

const MAX_SHARE_FILE_SIZE_BYTES = 30 * 1024 * 1024; // 30 MB

export const shareFile = async ({
  url,
  file,
}: ShareFileOptions): Promise<void> => {
  if (!file || file.size > MAX_SHARE_FILE_SIZE_BYTES) {
    await navigator.share({ url });
    return;
  }

  try {
    await navigator.share({ files: [file] });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return;
    console.error(
      "[shareFile] Failed to share file, falling back to URL share:",
      error,
    );
    try {
      await navigator.share({ url });
    } catch (urlShareError) {
      console.error("[shareFile] Failed to share URL:", urlShareError);
      throw urlShareError;
    }
  }
};

export const prepareFile = async (fileUrl: string, fileName: string) => {
  const res = await fetch(fileUrl);
  const blob = await res.blob();
  const file = new File([blob], fileName, {
    type: blob.type,
  });
  return file;
};
